import { useEffect, useRef, useState } from 'react';
import { useWalletDocumentCategories } from '@shared/hooks/use-wallet-documents';
import type { WalletDocumentCategory } from '@shared/api/wallet-documents';

interface WalletCategorySelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const FALLBACK_CATEGORIES: WalletDocumentCategory[] = [
  { code: 'AADHAAR', label: 'Aadhaar Card' },
  { code: 'PAN', label: 'PAN Card' },
  { code: 'LICENCE', label: 'Driving Licence' },
  { code: 'PASSPORT', label: 'Passport' },
  { code: 'BANK', label: 'Bank Account / Passbook' },
  { code: 'OTHER', label: 'Other Document' },
];

export function WalletCategorySelect({ value, onChange, className }: WalletCategorySelectProps) {
  const { data: serverCategories } = useWalletDocumentCategories();
  const categories =
    serverCategories && serverCategories.length > 0 ? serverCategories : FALLBACK_CATEGORIES;

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedCategory = categories.find((c) => c.code.toUpperCase() === value.toUpperCase()) || {
    code: value || 'OTHER',
    label: value || 'Other Document',
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className={`relative w-full ${className || ''}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-900 shadow-xs hover:border-gray-400 focus:border-brand-600 focus:outline-none transition-colors"
      >
        <span className="truncate">{selectedCategory.label}</span>
        <svg
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-40 mt-1.5 max-h-60 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl ring-1 ring-black/5 animate-in fade-in slide-in-from-top-1 duration-150">
          {categories.map((cat) => {
            const isSelected = cat.code.toUpperCase() === value.toUpperCase();
            return (
              <button
                key={cat.code}
                type="button"
                onClick={() => {
                  onChange(cat.code);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold transition-colors ${
                  isSelected
                    ? 'bg-brand-50 text-brand-800'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="truncate">{cat.label}</span>
                {isSelected ? (
                  <svg
                    className="h-4 w-4 text-brand-700 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
